import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "astra-docs-theme";

type Theme = "light" | "dark";

/**
 * Resolve the initial theme from localStorage, falling back to the OS
 * preference (honored via window.matchMedia). The tokens' CSS dark variant
 * also reacts to `prefers-color-scheme` when no explicit `data-theme` is
 * set, so first paint matches the OS either way.
 */
function initialTheme(): Theme {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable (restricted/private mode) — same guarded
    // fallback as the pre-paint script in index.html; the OS preference
    // below still applies.
  }
  if (stored === "light" || stored === "dark") return stored;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Non-fatal: storage restricted — the in-memory theme still applies
      // for this session and simply won't persist across reloads.
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle };
}
